import { PDFDocument, PDFForm, PDFTextField, PDFDropdown, PDFCheckBox, rgb, StandardFonts } from 'pdf-lib';

export interface FormFieldData {
  [fieldId: string]: any;
}

export interface PDFGenerationOptions {
  templateUrl?: string;
  formData: FormFieldData;
  formName: string;
  outputFilename?: string;
}

/**
 * PDF Generation Service
 * Generates filled PDF forms from form data
 */
class PDFGeneratorService {
  /**
   * Generate a filled PDF from form data
   * For now, creates a new PDF with form fields
   * In production, you'd load the template PDF and fill its fields
   */
  async generateFilledPDF(options: PDFGenerationOptions): Promise<Blob> {
    const { formData, formName, templateUrl } = options;

    try {
      // If we have a template URL, try to load and fill it
      if (templateUrl) {
        return await this.fillTemplatePDF(templateUrl, formData, formName);
      } else {
        // Otherwise, create a new PDF with the form data
        return await this.createNewPDF(formData, formName);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  }

  /**
   * Fill an existing PDF template with form data
   */
  private async fillTemplatePDF(
    templateUrl: string,
    formData: FormFieldData,
    formName: string
  ): Promise<Blob> {
    try {
      // Fetch the template PDF
      const response = await fetch(templateUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch template PDF: ${response.statusText}`);
      }

      const templateBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(templateBytes);

      // Try to get the form if it exists
      const form = pdfDoc.getForm();
      
      // Fill form fields if they exist
      this.fillPDFFormFields(form, formData);

      // Save the PDF
      const pdfBytes = await pdfDoc.save();
      return new Blob([pdfBytes], { type: 'application/pdf' });
    } catch (error) {
      console.error('Error filling template PDF:', error);
      // Fallback to creating new PDF if template loading fails
      return await this.createNewPDF(formData, formName);
    }
  }

  /**
   * Fill PDF form fields
   */
  private fillPDFFormFields(form: PDFForm, formData: FormFieldData): void {
    const fields = form.getFields();
    
    fields.forEach((field) => {
      const fieldName = field.getName();
      const normalizedName = fieldName.toLowerCase().replace(/[^a-z0-9]/g, '_');
      
      // Try to find matching data
      const matchingKey = Object.keys(formData).find(
        key => key.toLowerCase().replace(/[^a-z0-9]/g, '_') === normalizedName
      );

      if (matchingKey && formData[matchingKey] !== undefined && formData[matchingKey] !== '') {
        try {
          const value = formData[matchingKey];
          
          if (field instanceof PDFTextField) {
            field.setText(String(value));
          } else if (field instanceof PDFDropdown) {
            field.select(String(value));
          } else if (field instanceof PDFCheckBox) {
            field.check();
          }
        } catch (error) {
          console.warn(`Could not fill field ${fieldName}:`, error);
        }
      }
    });
  }

  /**
   * Create a new PDF with form data (fallback when template is not available)
   */
  private async createNewPDF(formData: FormFieldData, formName: string): Promise<Blob> {
    const pdfDoc = await PDFDocument.create();
    const page = pdfDoc.addPage([612, 792]); // US Letter size
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    let yPosition = 750;
    const lineHeight = 20;
    const margin = 50;
    let currentPage = page;

    // Add title
    currentPage.drawText(formName, {
      x: margin,
      y: yPosition,
      size: 18,
      font: boldFont,
      color: rgb(0, 0, 0),
    });
    yPosition -= 40;

    // Add form data
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        const label = this.formatFieldLabel(key);
        const text = `${label}: ${String(value)}`;
        
        // Check if we need a new page
        if (yPosition < 50) {
          currentPage = pdfDoc.addPage([612, 792]);
          yPosition = 750;
        }

        currentPage.drawText(text, {
          x: margin,
          y: yPosition,
          size: 12,
          font: font,
          color: rgb(0, 0, 0),
        });
        yPosition -= lineHeight;
      }
    });

    // Add footer to the last page
    const footerY = 30;
    currentPage.drawText(`Generated on ${new Date().toLocaleDateString()}`, {
      x: margin,
      y: footerY,
      size: 10,
      font: font,
      color: rgb(0.5, 0.5, 0.5),
    });

    const pdfBytes = await pdfDoc.save();
    return new Blob([pdfBytes], { type: 'application/pdf' });
  }

  /**
   * Format field label for display
   */
  private formatFieldLabel(fieldId: string): string {
    return fieldId
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Download PDF as file
   */
  downloadPDF(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'filled-form.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

export const pdfGenerator = new PDFGeneratorService();

